import { gql } from '@apollo/client';

import type { ForecastReadinessStatus, HistoricalOrdersProgress } from './getLastUpdatedAt';

/**
 * Drives the first-install banner and the setup page.
 *
 * One document, two root fields: `installHealth` carries the phase, the
 * demand-gate verdict and the liveness timestamps; `getLastUpdatedAt` is
 * selected only for `historicalOrdersProgress`, which carries the chunk
 * timeline (`installHealth` does not expose `totalChunks` or chunk dates).
 * Fetching both in one request keeps the banner on a single poll.
 */
export const GET_INSTALL_HEALTH = gql`
  query getInstallHealth {
    installHealth {
      installPhase
      phaseEnteredAt
      recentReadyAt
      heartbeatAt
      shopifyLastActivityAt
      connectingSubPhase
      lastErrorClass
      installChainStatus
      installChainCurrentStep
      canShowDemandRecommendations
      completedChunks
      totalChunks
      shopifyBatchesTotal
      shopifyBatchesCompleted
      currentChunkId
      ordersTotal
      ordersIngested
      startedAt
      chunkWindowStart
      chunkWindowEnd
      loadingOrdersSubState
      lastOrderImportedAt
    }
    getLastUpdatedAt {
      forecastReadinessStatus
      historicalOrdersProgress {
        totalChunks
        completedChunks
        currentChunkStartDate
        currentChunkEndDate
        oldestDateFetched
        targetStartDate
        hasFailedChunk
        startedAt
      }
    }
  }
`;

/** Forward-only loading spine plus the two terminal phases. */
export type InstallPhase =
  | 'CONNECTING'
  | 'CONNECTED'
  | 'LOADING_RECENT'
  | 'RECENT_READY'
  | 'LOADING_FULL'
  | 'FULL_READY'
  | 'DEGRADED'
  | 'BROWSE_ONLY';

/**
 * Failure classification — only set on terminal phases. Selects the `Why?`
 * explainer copy variant. Typed loosely: the backend does not constrain it
 * at the GraphQL layer.
 */
export type InstallErrorClass =
  | 'envelope'
  | 'deterministic'
  | 'transient'
  | 'credentials'
  | 'unknown';

/** Catalog-import banner sub-status during CONNECTING. NULL once the
 *  install advances past CONNECTING. Banner copy keys off this value. */
export type ConnectingSubPhase =
  | 'FETCHING_CATALOG'
  | 'CREATING_INVENTORY'
  | 'SYNCING_INITIAL_INVENTORY';

/** Sub-state inside the "Loading your order history" outer substep on
 *  the setup page. Distinguishes the Shopify-side preparation wait
 *  (``SHOPIFY_PREPARING``) from our-side ingest (``IMPORTING``) from
 *  the post-finalize gap (``FINALIZING`` — Shopify is done, the last
 *  chunk has finalized, and the post-bulk ``update_company_data`` task
 *  is in flight) from the done state (``DONE``). NULL when the merchant
 *  is not in an order-loading phase (CONNECTING, BROWSE_ONLY, DEGRADED). */
export type LoadingOrdersSubState = 'SHOPIFY_PREPARING' | 'IMPORTING' | 'FINALIZING' | 'DONE';

export interface InstallHealth {
  installPhase: InstallPhase | null;
  phaseEnteredAt: string | null;
  recentReadyAt: string | null;
  heartbeatAt: string | null;
  shopifyLastActivityAt: string | null;
  connectingSubPhase: ConnectingSubPhase | string | null;
  lastErrorClass: InstallErrorClass | string | null;
  installChainStatus: string | null;
  installChainCurrentStep: string | null;
  /** The demand-gate verdict — the single source of truth for whether
   *  demand-derived recommendations and columns may be shown. */
  canShowDemandRecommendations: boolean;
  completedChunks: number;
  /** Materialised chunk count, written by ``initiate_job`` after the
   *  ``BulkOrderIngestJob`` is created. Null before the job exists —
   *  consumers should fall back to preflight's estimate. Internal
   *  materialization sub-chunks — NOT merchant-facing. */
  totalChunks: number | null;
  /** Merchant-facing Shopify batch counts: one entry per planned
   *  bulkOperationRunQuery dispatch. ``shopifyBatchesTotal`` is null
   *  before preflight runs. The "Batch X of Y" UI label uses these. */
  shopifyBatchesTotal: number | null;
  shopifyBatchesCompleted: number;
  currentChunkId: string | null;
  /** Live order ingest counters, populated only when an active
   *  ``BulkOrderIngestJob`` row exists. Drive the "Loaded X of Y orders"
   *  aliveness signal in the banner and setup card. */
  ordersTotal: number | null;
  ordersIngested: number | null;
  /** Min(dispatched_at) over the job's chunk outbox rows — when our-side
   *  ingest began. Null when no active job. Used by the ETA helper to
   *  compute throughput against ordersIngested. */
  startedAt: string | null;
  /** Min/Max order createdAt within the in-flight chunk. Null when no
   *  chunk is currently in flight. Drives "Currently on Feb 2026" copy. */
  chunkWindowStart: string | null;
  chunkWindowEnd: string | null;
  /** Tri-state for the "Loading your order history" outer substep. Null
   *  when the merchant is not in an order-loading phase. */
  loadingOrdersSubState: LoadingOrdersSubState | null;
  /** max(ingested_at) over the active job's items. Drives "Last order
   *  imported · N sec ago" liveness. Null when no active job, or when
   *  no item has been ingested yet. */
  lastOrderImportedAt: string | null;
}

export interface GetInstallHealthResponse {
  installHealth: InstallHealth | null;
  getLastUpdatedAt: {
    historicalOrdersProgress: HistoricalOrdersProgress | null;
    forecastReadinessStatus: ForecastReadinessStatus | null;
  } | null;
}
